import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Wallet, TrendingDown, TrendingUp, Scale, BookOpen,
  FileText, BookCheck, ListOrdered, AlertTriangle, ArrowRight
} from 'lucide-react';
import { getAkuntansiDashboard } from '../../api';
import { sumberInfo, badgeSumber } from '../../utils/sumberJurnal';

// --- Stat Card Mini ---
function StatCardMini({ icon: Icon, label, value, color, sublabel }) {
  return (
    <div className="stat-card">
      <div className="stat-card-body">
        <div className="stat-card-content">
          <h5 className="stat-card-label">{label}</h5>
          <span className="stat-card-value">{value}</span>
          {sublabel && <span className="stat-card-sublabel">{sublabel}</span>}
        </div>
        <div className="stat-card-icon-col">
          <div className="stat-icon-shape" style={{ background: color + '18', color }}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Chart Card ---
function ChartCard({ title, subtitle, children }) {
  return (
    <div className="chart-card">
      <div className="chart-card-header">
        <h6 className="chart-card-title">{title}</h6>
        {subtitle && <span className="chart-card-subtitle">{subtitle}</span>}
      </div>
      <div className="chart-card-body">{children}</div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="tooltip-item" style={{ color: p.color }}>
            {p.name}: <strong>{p.value?.toLocaleString()}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export default function DashboardAkuntansi() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getAkuntansiDashboard()
      .then(res => setData(res.data))
      .catch(err => {
        console.error('Failed to load akuntansi dashboard', err);
        setError('Gagal memuat data dashboard akuntansi. Pastikan server backend berjalan.');
      })
      .finally(() => setLoading(false));
  }, []);

  const rupiah = (val) => {
    const n = Number(val) || 0;
    return `Rp ${n.toLocaleString('id-ID')}`;
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h2>Dashboard Akuntansi</h2>
          <p className="page-desc">Ringkasan kondisi keuangan perusahaan</p>
        </div>
        <div className="page-loading"><div className="spinner-lg" /><p>Memuat data dashboard...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header">
          <h2>Dashboard Akuntansi</h2>
          <p className="page-desc">Ringkasan kondisi keuangan perusahaan</p>
        </div>
        <div className="dash-error">
          <AlertTriangle size={32} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const labaRugi = Number(data.laba_rugi) || 0;
  const labaPositive = labaRugi >= 0;
  const balanceOk = Math.abs((Number(data.total_aset) || 0) - ((Number(data.total_kewajiban) || 0) + (Number(data.total_modal) || 0) + labaRugi)) < 1;

  const tren = (data.tren || []).map(t => ({
    label: t.label,
    jumlah: t.jumlah,
  }));

  const quickLinks = [
    { to: '/akuntansi/neraca', label: 'Neraca', icon: Scale },
    { to: '/akuntansi/laba-rugi', label: 'Laba Rugi', icon: TrendingUp },
    { to: '/akuntansi/buku-besar', label: 'Buku Besar', icon: BookCheck },
    { to: '/akuntansi/jurnal', label: 'Jurnal Umum', icon: BookOpen },
    { to: '/akuntansi/coa', label: 'Chart of Accounts', icon: ListOrdered },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h2>Dashboard Akuntansi</h2>
        <p className="page-desc">Ringkasan kondisi keuangan — per {new Date(data.tanggal + 'T00:00:00').toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>

      {/* Ringkasan posisi keuangan */}
      <div className="dash-stats-row" style={{ marginBottom: 20 }}>
        <div className="dash-stat-col">
          <StatCardMini icon={Wallet} label="Total Aset" value={rupiah(data.total_aset)} color="#11cdef"
            sublabel="Keseluruhan harta" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={TrendingDown} label="Total Kewajiban" value={rupiah(data.total_kewajiban)} color="#fb6340"
            sublabel="Total utang/hutang" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={Scale} label="Total Modal" value={rupiah(data.total_modal)} color="#5e72e4"
            sublabel="Modal pemilik" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={labaPositive ? TrendingUp : TrendingDown} label="Laba/Rugi Berjalan" value={rupiah(Math.abs(labaRugi))} color={labaPositive ? '#2dce89' : '#f5365c'}
            sublabel={labaPositive ? 'LABA' : 'RUGI'} />
        </div>
      </div>

      {/* Statistik aktivitas */}
      <div className="dash-stats-row" style={{ marginBottom: 20 }}>
        <div className="dash-stat-col">
          <StatCardMini icon={FileText} label="Jurnal Bulan Ini" value={data.jurnal_bulan_ini || 0} color="#11cdef"
            sublabel={`Dari ${data.total_jurnal || 0} total jurnal`} />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={BookOpen} label="Total Jurnal" value={data.total_jurnal || 0} color="#2dce89"
            sublabel="Semua transaksi" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={ListOrdered} label="Akun Aktif (COA)" value={data.total_coa || 0} color="#fb6340"
            sublabel="Chart of Accounts" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={Scale} label="Neraca Balance" value={balanceOk ? '✓ Balance' : '✗ Selisih'} color={balanceOk ? '#2dce89' : '#f5365c'}
            sublabel={balanceOk ? 'Aset = Kewajiban + Modal + Laba' : 'Perlu dicek'} />
        </div>
      </div>

      <div className="dash-charts-row" style={{ marginBottom: 20 }}>
        {/* Tren jurnal 6 bulan */}
        <div className="dash-chart-col-6">
          <ChartCard title="Tren Jurnal 6 Bulan" subtitle="Jumlah jurnal per bulan">
            {tren.length > 0 && tren.some(t => t.jumlah > 0) ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={tren} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                  <XAxis dataKey="label" stroke="#8898aa" fontSize={11} />
                  <YAxis stroke="#8898aa" fontSize={11} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="jumlah" name="Jurnal" radius={[6, 6, 0, 0]} barSize={36} fill="#11cdef" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-muted text-sm">Belum ada data jurnal dalam 6 bulan terakhir.</p>}
          </ChartCard>
        </div>

        {/* Jurnal terbaru */}
        <div className="dash-chart-col-6">
          <ChartCard title="Jurnal Terbaru" subtitle="5 transaksi terakhir">
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>No Jurnal</th>
                    <th>Tanggal</th>
                    <th>Sumber</th>
                    <th>Keterangan</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.jurnal_terbaru || []).map(j => (
                    <tr key={j.id}>
                      <td><strong style={{ fontSize: '0.8rem' }}>{j.no_jurnal}</strong></td>
                      <td style={{ fontSize: '0.8rem' }}>{j.tanggal}</td>
                      <td><span className="badge" style={{ ...badgeSumber(j.sumber), fontSize: '0.7rem' }}>{sumberInfo(j.sumber).label}</span></td>
                      <td style={{ fontSize: '0.8rem', maxWidth: 180 }}>
                        {j.keterangan || '-'}
                        {j.metode_pembayaran && (
                          <span style={{
                            display: 'inline-block', marginLeft: 6, padding: '1px 8px', borderRadius: 10,
                            fontSize: '0.65rem', fontWeight: 600, whiteSpace: 'nowrap',
                            background: '#ede9fe', color: '#5b21b6', verticalAlign: 'middle',
                          }}>Metode: {j.metode_pembayaran}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!data.jurnal_terbaru || data.jurnal_terbaru.length === 0) && (
                    <tr><td colSpan={4} className="text-center" style={{ padding: 30, color: 'var(--gray-400)' }}>Belum ada jurnal</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </div>
      </div>

      {/* Akses cepat */}
      <div className="chart-card">
        <div className="chart-card-header">
          <h6 className="chart-card-title">Akses Cepat</h6>
          <span className="chart-card-subtitle">Buka laporan akuntansi</span>
        </div>
        <div className="chart-card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {quickLinks.map(q => {
            const Icon = q.icon;
            return (
              <button key={q.to} className="btn btn-secondary" onClick={() => navigate(q.to)}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon size={16} />{q.label}<ArrowRight size={14} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}